import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useCustomerFetch, Card, SectionLabel } from "../../components/customer-app/ui";
import LanguageSelector from "../../components/LanguageSelector";
import { toast } from "sonner";
import { Phone, MapPin, LogOut, Sun, ShieldCheck, Gift, Copy, Share2, Users, ChevronLeft, ChevronRight, ShieldAlert, Globe } from "lucide-react";

const REFERRAL_STATUS_LABEL = {
  open: "Signed up", accepted: "In progress", on_hold: "On hold", dropped: "Closed", converted: "Converted!",
};

export default function CustomerProfile() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const nav = useNavigate();
  const { data: me } = useCustomerFetch("/customer-app/me");
  const { data: referrals } = useCustomerFetch("/customer-app/referrals");

  async function doLogout() {
    if (!window.confirm(t("profile.logoutConfirm"))) return;
    await logout();
    nav("/app/login");
  }

  const code = me?.user?.referral_code;
  const shareText = code
    ? `Go solar with Purvodaya Solar! Use my referral code ${code} when you buy a new installation on the app: ${window.location.origin}/app/login?ref=${code}`
    : "";

  async function share() {
    if (navigator.share) {
      try { await navigator.share({ title: "Purvodaya Solar", text: shareText }); } catch (e) { /* cancelled */ }
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
    }
  }

  function copyCode() {
    navigator.clipboard.writeText(code);
    toast.success("Referral code copied!");
  }

  return (
    <div className="space-y-5 pb-4" data-testid="customer-profile">
      <button onClick={() => nav(-1)} data-testid="profile-back-btn" className="inline-flex items-center gap-1 text-xs font-bold text-slate-500">
        <ChevronLeft className="w-3.5 h-3.5" /> {t("common.back")}
      </button>
      <div className="flex flex-col items-center pt-4">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#0D4721] via-[#15803D] to-[#0B3C1C] flex items-center justify-center text-white text-2xl font-extrabold">
          {(user?.name || "C")[0].toUpperCase()}
        </div>
        <div className="text-lg font-extrabold text-slate-900 mt-3">{user?.name || "Solar Customer"}</div>
        <div className="text-sm text-slate-500 font-mono flex items-center gap-1.5 mt-1"><Phone className="w-3.5 h-3.5" /> {user?.phone}</div>
      </div>

      {/* Refer a Friend */}
      <Card className="p-4 bg-gradient-to-br from-amber-50 to-emerald-50 border-amber-200/70" data-testid="refer-a-friend-card">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-amber-400/90 flex items-center justify-center flex-shrink-0"><Gift className="w-4.5 h-4.5 text-white" /></div>
          <div>
            <div className="text-sm font-extrabold text-slate-900">{t("profile.referFriend")}</div>
            <div className="text-[11px] text-slate-500">{t("profile.referFriendSub")}</div>
          </div>
        </div>
        {code ? (
          <>
            <div className="mt-3 flex items-center gap-2">
              <div data-testid="referral-code" className="flex-1 bg-white rounded-xl border-2 border-dashed border-amber-300 px-3 py-2.5 text-center font-mono text-lg font-extrabold tracking-widest text-[#0D4721]">
                {code}
              </div>
              <button onClick={copyCode} data-testid="referral-copy-btn" className="w-11 h-11 flex-shrink-0 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:text-[#0D4721]">
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <button onClick={share} data-testid="referral-share-btn"
              className="w-full mt-3 bg-[#0D4721] text-white py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2">
              <Share2 className="w-4 h-4" /> {t("profile.shareFriend")}
            </button>
          </>
        ) : (
          <div className="mt-3 text-xs text-slate-400 text-center py-2">Loading your code…</div>
        )}
      </Card>

      {referrals?.length > 0 && (
        <div>
          <SectionLabel className="mb-2 flex items-center gap-1.5"><Users className="w-3 h-3" /> {t("profile.myReferrals")} ({referrals.length})</SectionLabel>
          <div className="space-y-2">
            {referrals.map((r) => (
              <Link key={r.lead_id} to={`/app/referrals/${r.lead_id}`} data-testid={`referral-row-${r.lead_id}`}>
                <Card className="p-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">{r.customer_name}</div>
                    <div className="text-[11px] text-slate-400">{new Date(r.created_at).toLocaleDateString()}</div>
                    {r.is_serviceable === false ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 px-1.5 py-0.5 rounded-md mt-1">
                        <ShieldAlert className="w-2.5 h-2.5" /> Non Serviceable Area
                      </span>
                    ) : r.installation_status_label ? (
                      <div className="text-[11px] text-emerald-700 font-semibold mt-0.5">{r.installation_status_label}</div>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className={"text-[11px] font-bold px-2.5 py-1 rounded-full " + (r.status === "converted" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600")}>
                      {REFERRAL_STATUS_LABEL[r.status] || r.status}
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div>
        <SectionLabel className="mb-2">{t("profile.linkedAddresses")}</SectionLabel>
        {!me?.customers?.length ? (
          <Card className="p-4 text-sm text-slate-500 text-center">{t("profile.noAddresses")}</Card>
        ) : (
          <div className="space-y-2">
            {me.customers.map((c) => (
              <Card key={c.id} className="p-3.5 flex items-start gap-2.5" data-testid={`profile-address-${c.id}`}>
                <MapPin className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-slate-800">{c.customer_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{c.address}</div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <div>
        <SectionLabel className="mb-2 flex items-center gap-1.5"><Globe className="w-3 h-3" /> {t("profile.preferredLanguage")}</SectionLabel>
        <Card className="p-3.5">
          <LanguageSelector activeClass="bg-[#0D4721] text-white border-[#0D4721]" testidPrefix="customer-profile-lang" />
        </Card>
      </div>

      <Card className="p-4 flex items-center gap-3 bg-emerald-50/50 border-emerald-200">
        <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" />
        <div className="text-xs text-emerald-800">{t("profile.secureNote")}</div>
      </Card>

      <button
        onClick={doLogout}
        data-testid="profile-logout"
        className="w-full flex items-center justify-center gap-2 border-2 border-red-200 text-red-600 py-3.5 rounded-xl text-sm font-bold hover:bg-red-50"
      >
        <LogOut className="w-4 h-4" /> {t("common.logout")}
      </button>

      <div className="text-center text-[11px] text-slate-400 flex items-center justify-center gap-1.5 pt-2">
        <Sun className="w-3 h-3" /> Purvodaya Solar App · v1.0
      </div>
    </div>
  );
}
